<template>
    <div class="flex flex-col h-full text-sm text-white space-y-3 p-2">
        <!-- Toolbar -->
        <div class="flex gap-2 flex-wrap">
            <button @click="$refs.importInput.click()"
                class="px-3 py-1 rounded bg-ui-active text-ui-bg text-xs font-semibold hover:opacity-90">
                Import components
            </button>
            <button @click="exportAll" :disabled="components.length === 0"
                class="px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-xs disabled:opacity-40">
                Export all
            </button>
            <input ref="importInput" type="file" accept=".hasp-component,.hasp-components"
                class="hidden" @change="handleImportFile" />
        </div>

        <!-- Component list -->
        <div v-if="components.length === 0" class="text-white/50 text-xs py-4 text-center">
            No custom components saved yet.
        </div>

        <div v-for="comp in components" :key="comp.id"
            class="flex items-start gap-3 bg-ui-bg-input rounded p-2 border border-ui-active/20 hover:border-ui-active/40 transition-colors">
            <!-- Icon -->
            <icon-editor
                v-if="editingId === comp.id"
                :model-value="editIcon ?? comp.icon"
                :size="48"
                @update:model-value="editIcon = $event"
            />
            <div v-else class="w-12 h-12 rounded bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                <img v-if="comp.icon" :src="comp.icon" class="w-full h-full object-contain" />
                <span v-else class="text-2xl select-none">🧩</span>
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
                <div v-if="editingId !== comp.id">
                    <div class="font-semibold text-sm truncate">{{ comp.name }}</div>
                    <div class="text-white/50 text-xs mt-0.5 line-clamp-2">{{ comp.description || '—' }}</div>
                </div>
                <div v-else class="space-y-1">
                    <input v-model="editName" type="text" class="w-full bg-ui-bg border border-ui-active/30 rounded px-1 py-0.5 text-xs focus:outline-none focus:border-ui-active" />
                    <textarea v-model="editDescription" rows="2" class="w-full bg-ui-bg border border-ui-active/30 rounded px-1 py-0.5 text-xs focus:outline-none focus:border-ui-active resize-none" />
                </div>
            </div>

            <!-- Actions -->
            <div class="flex flex-col gap-1 flex-shrink-0 text-xs">
                <template v-if="editingId !== comp.id">
                    <button @click="startEdit(comp)" class="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Edit</button>
                    <button @click="$emit('edit-design', comp)" class="px-2 py-0.5 rounded bg-yellow-700 hover:bg-yellow-600 text-yellow-100">Edit design</button>
                    <button @click="exportOne(comp)" class="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Export</button>
                    <button @click="confirmDelete(comp)" class="px-2 py-0.5 rounded bg-red-800 hover:bg-red-700 text-red-200">Delete</button>
                </template>
                <template v-else>
                    <button @click="saveEdit(comp)" class="px-2 py-0.5 rounded bg-ui-active text-ui-bg hover:opacity-90">Save</button>
                    <button @click="cancelEdit" class="px-2 py-0.5 rounded bg-white/10 hover:bg-white/20">Cancel</button>
                </template>
            </div>
        </div>

        <!-- Delete confirm -->
        <div v-if="deleteTarget" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div class="bg-ui-bg border border-ui-active/30 rounded-lg p-4 w-80 space-y-3 text-white text-sm shadow-xl">
                <div class="font-bold">Delete "{{ deleteTarget.name }}"?</div>
                <div class="text-white/50 text-xs">This cannot be undone. Placed instances are not affected.</div>
                <div class="flex justify-end gap-2">
                    <button @click="deleteTarget = null" class="px-3 py-1 rounded bg-white/10 hover:bg-white/20">Cancel</button>
                    <button @click="doDelete" class="px-3 py-1 rounded bg-red-700 hover:bg-red-600 font-semibold">Delete</button>
                </div>
            </div>
        </div>

        <!-- Import results toast -->
        <div v-if="importToast" class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-ui-bg text-white text-xs px-4 py-2 rounded shadow-lg z-50 border border-ui-active/30">
            {{ importToast }}
        </div>
    </div>
</template>

<script>
import { toRaw } from 'vue';
import {
    getAllCustomComponents,
    addCustomComponent,
    updateCustomComponent,
    deleteCustomComponent,
} from '../../utils/customComponentDB.js';
import IconEditor from '../form/IconEditor.vue';

export default {
    name: 'CustomComponentManager',
    components: { IconEditor },
    props: {
        refreshTrigger: { type: Number, default: 0 },
    },
    emits: ['updated', 'edit-design'],
    data() {
        return {
            components: [],
            editingId: null,
            editName: '',
            editDescription: '',
            editIcon: null,
            deleteTarget: null,
            importToast: null,
            _toastTimer: null,
        };
    },
    watch: {
        refreshTrigger() {
            this.load();
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            this.components = await getAllCustomComponents();
        },
        startEdit(comp) {
            this.editingId = comp.id;
            this.editName = comp.name;
            this.editDescription = comp.description || '';
            this.editIcon = null;
        },
        cancelEdit() {
            this.editingId = null;
        },
        async saveEdit(comp) {
            const raw = JSON.parse(JSON.stringify(toRaw(comp)));
            const updated = {
                ...raw,
                name: this.editName.trim() || raw.name,
                description: this.editDescription.trim(),
                icon: this.editIcon ?? raw.icon,
            };
            await updateCustomComponent(updated);
            this.editingId = null;
            await this.load();
            this.$emit('updated');
        },
        confirmDelete(comp) {
            this.deleteTarget = comp;
        },
        async doDelete() {
            await deleteCustomComponent(this.deleteTarget.id);
            this.deleteTarget = null;
            await this.load();
            this.$emit('updated');
        },
        exportOne(comp) {
            this._downloadJson([comp], comp.name + '.hasp-component');
        },
        exportAll() {
            this._downloadJson(this.components, 'components.hasp-components');
        },
        _downloadJson(data, filename) {
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        async handleImportFile(event) {
            const file = event.target.files[0];
            if (!file) return;
            event.target.value = '';
            try {
                const text = await file.text();
                const incoming = JSON.parse(text);
                const items = Array.isArray(incoming) ? incoming : [incoming];
                await this._importItems(items);
            } catch (e) {
                this.showToast('Import failed: invalid file.');
            }
        },
        async _importItems(items) {
            const existing = await getAllCustomComponents();
            const existingMap = new Map(existing.map(c => [c.id, c]));

            let imported = 0, skipped = 0, overwritten = 0;
            const conflicts = [];

            for (const item of items) {
                if (!item.id || !item.name) continue;
                const current = existingMap.get(item.id);
                if (!current) {
                    await addCustomComponent(item);
                    imported++;
                } else {
                    const same = JSON.stringify(current.objects) === JSON.stringify(item.objects)
                        && current.name === item.name;
                    if (same) {
                        skipped++;
                    } else {
                        conflicts.push(item);
                    }
                }
            }

            if (conflicts.length > 0) {
                await this._resolveConflicts(conflicts, { imported, skipped, overwritten });
            } else {
                await this.load();
                this.$emit('updated');
                this.showToast(this._summaryText(imported, skipped, overwritten));
            }
        },
        async _resolveConflicts(conflicts, counts) {
            let overwritten = counts.overwritten;
            let skipped = counts.skipped;
            let allYes = false;
            let allNo = false;

            for (const item of conflicts) {
                if (allNo) { skipped++; continue; }
                if (allYes) {
                    await updateCustomComponent(item);
                    overwritten++;
                    continue;
                }

                const choice = await this._askOverwrite(item.name, conflicts.length > 1);
                if (choice === 'yes') {
                    await updateCustomComponent(item);
                    overwritten++;
                } else if (choice === 'yes-all') {
                    allYes = true;
                    await updateCustomComponent(item);
                    overwritten++;
                } else if (choice === 'no-all') {
                    allNo = true;
                    skipped++;
                } else {
                    skipped++;
                }
            }

            await this.load();
            this.$emit('updated');
            this.showToast(this._summaryText(counts.imported, skipped, overwritten));
        },
        _askOverwrite(name, hasMore) {
            return new Promise((resolve) => {
                const buttons = hasMore
                    ? [
                        { label: 'Yes', value: 'yes' },
                        { label: 'Yes to all', value: 'yes-all' },
                        { label: 'No', value: 'no' },
                        { label: 'No to all', value: 'no-all' },
                    ]
                    : [
                        { label: 'Overwrite', value: 'yes' },
                        { label: 'Skip', value: 'no' },
                    ];

                const msg = buttons.map((b, i) => `${i + 1}: ${b.label}`).join('\n');
                const answer = window.prompt(
                    `"${name}" already exists with different content.\nChoose:\n${msg}`,
                    '1'
                );
                const idx = parseInt(answer, 10) - 1;
                resolve(buttons[idx]?.value ?? 'no');
            });
        },
        _summaryText(imported, skipped, overwritten) {
            const parts = [];
            if (imported > 0) parts.push(`${imported} imported`);
            if (overwritten > 0) parts.push(`${overwritten} overwritten`);
            if (skipped > 0) parts.push(`${skipped} skipped`);
            return parts.length ? parts.join(', ') : 'Nothing changed.';
        },
        showToast(msg) {
            this.importToast = msg;
            clearTimeout(this._toastTimer);
            this._toastTimer = setTimeout(() => { this.importToast = null; }, 3500);
        },
    },
};
</script>
